/// Utility functions for COA Release processing
export const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/,/g, '');
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(/%/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
// Converts any value to a displayable string, treating null and undefined as empty strings
export const toDisplayText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

export const normalizeComparableText = (value: string): string =>
  value.toLowerCase().trim().replace(/\s+/g, ' ');

// Normalizes text for "Not Detected" equivalence by removing all whitespace and comparing against known variants
const normalizeNotDetectedText = (value: string): string => normalizeComparableText(value).replace(/\s+/g, '');

const isNotDetectedEquivalent = (value: string): boolean => {
  const normalized = normalizeNotDetectedText(value);
  return normalized === 'nd' || normalized === 'notdetected';
};

// Extracts a numeric branch rank from text by looking for trailing numbers, optionally preceded by keywords like "branch", "variation", "var", or "v"
export const extractBranchRank = (...values: Array<string | undefined | null>): number => {
  for (const value of values) {
    const text = String(value || '').trim();
    if (!text) continue;
    const trailingNumber = text.match(/(?:branch|variation|var|v)?\s*[-_#:]?\s*(\d+)\s*$/i);
    if (trailingNumber) {
      const rank = Number(trailingNumber[1]);
      if (Number.isFinite(rank)) return rank;
    }
  }
  return 0;
};

// Builds a label for a match option based on its analysis text, defaulting to "(No Analysis)" if the analysis is missing or empty
export const buildMatchOptionLabel = (option: { analysis?: string | null }): string =>
  String(option.analysis || '').trim() || '(No Analysis)';

// Sorts match options primarily by descending score, then by descending branch rank, and finally by alphabetical order of the analysis text
export const sortMatchOptions = <T extends { score: number; branchRank: number; analysis?: string | null }>(
  options: T[]
): T[] =>
  [...options].sort((a, b) => {
    const scoreDiff = Number(b.score || 0) - Number(a.score || 0);
    if (scoreDiff !== 0) return scoreDiff;
    const branchDiff = Number(b.branchRank || 0) - Number(a.branchRank || 0);
    if (branchDiff !== 0) return branchDiff;
    return String(a.analysis || '').localeCompare(String(b.analysis || ''));
  });

  // Normalizes unit text by converting to lowercase, replacing micro symbols with 'u', removing whitespace, replacing 'per' with '/', and trimming
export const normalizeUnitText = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .replace(/[μµ]/g, 'u')
    .replace(/\s+/g, '')
    .replace(/per/g, '/')
    .trim();

// Normalizes unit tokens by first normalizing the text and then replacing known variants with standardized forms (e.g., "micrograms" to "ug", "kilograms" to "kg", etc.)
const normalizeUnitToken = (value: string): string =>
  normalizeUnitText(value)
    .replace(/micrograms?|mcg/g, 'ug')
    .replace(/kilogram|kgs?/g, 'kg')
    .replace(/grams?/g, 'g')
    .replace(/milligrams?/g, 'mg')
    .replace(/kilojoules?/g, 'kj')
    .replace(/joules?/g, 'j')
    .replace(/percent|pct/g, '%');

// Retrieves the base conversion factor for known units, categorizing them into families (e.g., mass, energy, ratio) and providing a factor to convert to a common base unit within that family
const getKnownUnitBaseFactor = (unit: string): { family: string; factor: number } | null => {
  const normalized = normalizeUnitToken(unit);
  const factors: Record<string, { family: string; factor: number }> = {
    kg: { family: 'mass', factor: 1000 },
    g: { family: 'mass', factor: 1 },
    mg: { family: 'mass', factor: 0.001 },
    ug: { family: 'mass', factor: 0.000001 },
    kj: { family: 'energy', factor: 1000 },
    j: { family: 'energy', factor: 1 },
    kcal: { family: 'energy', factor: 4184 },
    cal: { family: 'energy', factor: 4.184 },
    '%': { family: 'ratio', factor: 1 },
  };
  return factors[normalized] || null;
};
// Parses a unit expression into its numerator and denominator components, normalizing the text and removing any parenthetical or bracketed content. Returns null if the expression cannot be parsed.
const parseUnitExpression = (unit: string): { numerator: string; denominator: string } | null => {
  const normalized = normalizeUnitText(unit)
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '');
  if (!normalized) return null;
  const [rawNumerator, ...rawDenominatorParts] = normalized.split('/');
  const numeratorMatch = rawNumerator.match(/[a-z%]+/i);
  if (!numeratorMatch) return null;
  return {
    numerator: normalizeUnitToken(numeratorMatch[0]),
    denominator: normalizeUnitToken(rawDenominatorParts.join('/')),
  };
};
// Extracts comparable tokens from a unit expression by normalizing the text, removing parenthetical and bracketed content, and matching sequences of letters and percent signs. Returns an array of normalized tokens.
const getUnitExpressionTokens = (unit: string): string[] => {
  const normalized = normalizeUnitText(unit)
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '');
  const matches = normalized.match(/[a-z%]+/gi) || [];
  return matches.map((token) => normalizeUnitToken(token)).filter(Boolean);
};
// Determines if two unit expressions share any comparable tokens, which can indicate that they may be related or convertible even if they are not directly compatible. This is used to assess whether a mismatch in units might still be considered aligned based on shared components.
const unitsShareComparableToken = (fromUnit: string, toUnit: string): boolean => {
  const fromTokens = new Set(getUnitExpressionTokens(fromUnit));
  const toTokens = getUnitExpressionTokens(toUnit);
  return toTokens.some((token) => fromTokens.has(token));
};
// Calculates the conversion factor between two units if they are directly convertible based on known factors or if they are the same unit. Handles specific hardcoded conversions (e.g., mg/kg to μg) and also attempts to parse and compare unit expressions for more complex cases. Returns null if the units cannot be converted.
export const getUnitConversionFactor = (fromUnit: string, toUnit: string): number | null => {
  const normalizedFrom = normalizeUnitText(fromUnit);
  const normalizedTo = normalizeUnitText(toUnit);
  if (!normalizedFrom || !normalizedTo) return null;
  if (normalizedFrom === normalizedTo) return 1;

  if (normalizedFrom === 'mg/kg' && normalizedTo === 'μg') return 100;
  if (normalizedFrom === 'mg/kg' && normalizedTo === 'ug') return 100;
  if (normalizedFrom === 'mg/kg' && normalizedTo === 'µg/100g') return 100;
  if (normalizedFrom === 'mg/kg' && normalizedTo === 'ug/100g') return 100;
  if (normalizedFrom === 'mg/kg' && normalizedTo === 'mg') return 0.1;
  if (normalizedFrom === 'mg/kg' && normalizedTo === 'mg/100g') return 0.1;
  if (normalizedFrom === '%' && normalizedTo === 'mg') return 1000;

  // "mg/100g" ↔ qualified-percentage units (e.g., "% TFA", "% fat"):
  // 1% = 1 g/100g = 1000 mg/100g, so mg/100g → % factor is 0.001 and the reverse is 1000
  if (normalizedFrom === 'mg/100g' && normalizedTo.startsWith('%')) return 0.001;
  if (normalizedFrom.startsWith('%') && normalizedTo === 'mg/100g') return 1000;

  const from = parseUnitExpression(fromUnit);
  const to = parseUnitExpression(toUnit);
  if (!from || !to) return null;

  const denominatorCompatible =
    from.denominator === to.denominator || !from.denominator || !to.denominator;
  if (!denominatorCompatible) return null;

  const fromFactor = getKnownUnitBaseFactor(from.numerator);
  const toFactor = getKnownUnitBaseFactor(to.numerator);
  if (fromFactor && toFactor && fromFactor.family === toFactor.family) {
    return fromFactor.factor / toFactor.factor;
  }

  if (from.numerator === to.numerator) return 1;
  return null;
};
// Formats a converted numeric value to a string with up to 6 decimal places, ensuring that it is a finite number. If the value is not finite, an empty string is returned.
const formatConvertedNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '';
  return Number(value.toFixed(6)).toString();
};

export interface UnitConversionResult {
  valueRaw: string;
  unit: string;
  converted: boolean;
  aligned: boolean;
  reason: string;
}
// Converts a raw test value from one unit to another, applying the appropriate conversion factor if the units are compatible. Handles complex expressions with comparators and multiple values separated by commas. Returns an object containing the converted value, the target unit, whether a conversion was performed, whether the units are considered aligned, and any reason for misalignment if applicable.
export const convertTestValueRawToUnit = (
  testValueRaw: string,
  fromUnit: string,
  toUnit: string
): UnitConversionResult => {
  const factor = getUnitConversionFactor(fromUnit, toUnit);
  if (factor === null) {
    const normalizedFrom = normalizeUnitText(fromUnit);
    const normalizedTo = normalizeUnitText(toUnit);
    const hasMismatch = !!normalizedFrom && !!normalizedTo && normalizedFrom !== normalizedTo;
    const hasSharedToken = hasMismatch && unitsShareComparableToken(fromUnit, toUnit);
    return {
      valueRaw: testValueRaw,
      unit: fromUnit || toUnit,
      converted: false,
      aligned: !hasMismatch || hasSharedToken,
      reason: hasMismatch && !hasSharedToken ? `Cannot convert unit from ${fromUnit} to ${toUnit}` : '',
    };
  }

  const normalizedFrom = normalizeUnitText(fromUnit);
  const normalizedTo = normalizeUnitText(toUnit);
  if (!testValueRaw || normalizedFrom === normalizedTo) {
    return { valueRaw: testValueRaw, unit: toUnit || fromUnit, converted: false, aligned: true, reason: '' };
  }

  const convertedParts = String(testValueRaw || '').split(',').map((part) => {
    const token = part.trim();
    if (!token) return token;
    const comparatorMatch = token.match(/^([<>]=?|≤|≥)\s*(-?\d+(?:\.\d+)?)$/);
    if (comparatorMatch) {
      return `${comparatorMatch[1]}${formatConvertedNumber(Number(comparatorMatch[2]) * factor)}`;
    }
    const numberMatch = token.match(/^-?\d+(?:\.\d+)?$/);
    if (numberMatch) {
      return formatConvertedNumber(Number(token) * factor);
    }
    return token;
  });

  return { valueRaw: convertedParts.join(', '), unit: toUnit || fromUnit, converted: factor !== 1, aligned: true, reason: '' };
};
// Returns true if the text contains at least one CJK (Chinese) character — used to detect bilingual spec descriptions
const hasCJKCharacter = (text: string): boolean => /[一-龥]/.test(text);

// Evaluates a single token from the test value against the specification min and max, handling numeric values, comparators, and string values. Returns an object indicating whether the token passed the evaluation and any reason for failure if it did not pass.
const evaluateSingleToken = (
  token: string,
  specMin: number | null,
  specMax: number | null,
  specMinText: string,
  specMaxText: string
): { passed: boolean; reason: string } => {
  const raw = token.trim();
  if (!raw) return { passed: true, reason: '' };

  const normalizedToken = normalizeComparableText(raw);

  // Allow an optional unit suffix (e.g., ">= 60%" or "<8mg") so the numeric threshold is still extracted
  const comparatorMatch = raw.match(/^([<>]=?|≤|≥)\s*(-?\d+(?:\.\d+)?)(?:[a-z%]+\s*)?$/i);
  if (comparatorMatch) {
    const op = comparatorMatch[1];
    const threshold = Number(comparatorMatch[2]);
    if (!Number.isFinite(threshold)) {
      return { passed: false, reason: `${raw} is not a valid number` };
    }
    if (op === '<' || op === '<=' || op === '≤') {
      if (specMin !== null && specMax !== null) {
        const passed = threshold > specMin && threshold <= specMax;
        return { passed, reason: passed ? '' : `${raw} must be greater than Spec Min (${specMin}) and less than or equal to Spec Max (${specMax})` };
      }
      if (specMax !== null) {
        const passed = threshold <= specMax;
        return { passed, reason: passed ? '' : `${raw} is not lower than or equal to Spec Max (${specMax})` };
      }
      if (specMin !== null) {
        const passed = threshold > specMin;
        return { passed, reason: passed ? '' : `${raw} must be greater than Spec Min (${specMin})` };
      }
      return { passed: false, reason: `Spec Min and Spec Max are missing for ${raw}` };
    }
    if (specMax === null && specMin !== null) {
      const passed = op === '>' ? threshold > specMin : threshold >= specMin;
      return { passed, reason: passed ? '' : `${raw} is not higher than Spec Min (${specMin})` };
    }
    if (specMax === null) {
      return { passed: false, reason: `Spec Max and Spec Min are missing for ${raw}` };
    }
    const passed = op === '>' ? threshold > specMax : threshold >= specMax;
    return { passed, reason: passed ? '' : `${raw} is not higher than Spec Max (${specMax})` };
  }
// Attempt to parse as a plain number for direct comparison against numeric specs
  const plainNumber = toNumber(raw);
  if (plainNumber !== null) {
    if (plainNumber < 0.4 && specMax !== null && specMax > 0.4) {
      return { passed: true, reason: '' };
    }
    const reasons: string[] = [];
    if (specMin !== null && plainNumber < specMin) reasons.push(`${plainNumber} below Spec Min (${specMin})`);
    if (specMax !== null && plainNumber > specMax) reasons.push(`${plainNumber} above Spec Max (${specMax})`);
    return { passed: reasons.length === 0, reason: reasons.join('; ') };
  }
// For string values, compare against spec texts and apply special rules for longer specs and "Not Detected" equivalence
  const specCandidates = [specMinText, specMaxText].map((v) => normalizeComparableText(v)).filter(Boolean);
  if (specCandidates.length === 0) {
    return { passed: false, reason: `Spec text is missing for string value ${raw}` };
  }
  if (isNotDetectedEquivalent(raw) && specCandidates.some((c) => isNotDetectedEquivalent(c))) {
    return { passed: true, reason: '' };
  }
  // Pass when testValue is a word token embedded in a bilingual spec description.
  // Handles cases like: spec "Absent 未检出" + testValue "Absent", spec "Grade A A等级" + testValue "A"
  if (specCandidates.some((c) => hasCJKCharacter(c) && c.split(/\s+/).includes(normalizedToken))) {
    return { passed: true, reason: '' };
  }
  const maxSpecLength = specCandidates.reduce((max, c) => Math.max(max, c.length), 0);
  if (maxSpecLength > 10) {
    if (normalizedToken.includes('matching')) return { passed: true, reason: '' };
    return { passed: false, reason: `String value ${raw} must contain 'matching' when spec text length is greater than 10` };
  }
  const passed = specCandidates.includes(normalizedToken);
  return { passed, reason: passed ? '' : `String value ${raw} does not match Spec Min/Max` };
};
// Evaluates a raw test value against the specification min and max, handling multiple comma-separated values and applying the single token evaluation to each. Returns an object indicating whether all tokens passed the evaluation, any reasons for failure, and a numeric value if there is exactly one token that can be parsed as a number.
export const evaluateTestValueAgainstSpec = (
  testValueRaw: string,
  specMinRaw: number | string | null,
  specMaxRaw: number | string | null
): { passed: boolean; reason: string; numericValue: number | null } => {
  const values = String(testValueRaw || '').split(',').map((v) => v.trim()).filter(Boolean);
  if (values.length === 0) {
    return { passed: false, reason: 'TestValue is empty', numericValue: null };
  }

  // Extract numeric bound from spec values that may carry a comparator prefix (e.g., "≤ 12" → 12, ">= 60" → 60)
  const extractSpecBound = (r: number | string | null): number | null => {
    if (typeof r === 'number') return Number.isFinite(r) ? r : null;
    if (typeof r !== 'string') return null;
    const stripped = r.trim().replace(/,/g, '').replace(/^([<>]=?|[≤≥])\s*/, '');
    const parsed = Number(stripped.replace(/%/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  };
  const specMin = extractSpecBound(specMinRaw);
  const specMax = extractSpecBound(specMaxRaw);
  const specMinText = toDisplayText(specMinRaw);
  const specMaxText = toDisplayText(specMaxRaw);

  const reasons: string[] = [];
  values.forEach((token) => {
    const result = evaluateSingleToken(token, specMin, specMax, specMinText, specMaxText);
    if (!result.passed && result.reason) reasons.push(result.reason);
  });

  const numericValue = values.length === 1 ? toNumber(values[0]) : null;
  return { passed: reasons.length === 0, reason: reasons.join('; '), numericValue };
};
